import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Shield, X, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CONSENT_KEY, CONSENT_VERSION } from '../lib/privacyConsent';

const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    personalization: false
  });

  useEffect(() => {
    // Check if user has already consented
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Check if consent version matches
        if (parsed.version === CONSENT_VERSION) {
          setPreferences(parsed.preferences);
          applyConsent(parsed.preferences);
          return;
        }
      } catch {
        // Invalid stored data, show banner
      }
    }
    // Show banner after a short delay for better UX
    const timer = setTimeout(() => setShowBanner(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const applyConsent = (prefs) => {
    // Apply consent to Google services if available
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': prefs.analytics ? 'granted' : 'denied',
        'ad_storage': prefs.marketing ? 'granted' : 'denied',
        'ad_personalization': prefs.personalization ? 'granted' : 'denied',
        'ad_user_data': prefs.marketing ? 'granted' : 'denied'
      });
    }

    // Dispatch custom event for other scripts to listen
    window.dispatchEvent(new CustomEvent('cookieConsentUpdate', { 
      detail: prefs 
    }));
  };

  const saveConsent = (prefs) => {
    const consentData = {
      version: CONSENT_VERSION,
      preferences: prefs,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
    applyConsent(prefs);
    setShowBanner(false);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      personalization: true
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const handleRejectNonEssential = () => {
    const essentialOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      personalization: false
    };
    setPreferences(essentialOnly);
    saveConsent(essentialOnly);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Cannot disable necessary cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const cookieTypes = [
    {
      key: 'necessary',
      title: 'Essential Cookies',
      description: 'Required for the website to function properly. These cannot be disabled.',
      examples: 'Login sessions, security tokens, shopping cart',
      required: true
    },
    {
      key: 'analytics',
      title: 'Analytics Cookies',
      description: 'Help us understand how visitors interact with our website to improve user experience.',
      examples: 'Google Analytics, page views, session duration'
    },
    {
      key: 'marketing',
      title: 'Marketing Cookies',
      description: 'Used to deliver relevant advertisements and track ad campaign performance.',
      examples: 'Google Ads, Facebook Pixel, retargeting'
    },
    {
      key: 'personalization',
      title: 'Personalization Cookies',
      description: 'Remember your preferences and provide personalized content and recommendations.',
      examples: 'Language preferences, theme settings, music recommendations'
    }
  ];

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed inset-x-3 z-[100] bottom-[calc(var(--rastyle-mobile-media-stack-height,4rem)+0.75rem)] sm:inset-x-auto sm:bottom-6 sm:left-6 sm:w-[min(30rem,calc(100vw-3rem))]"
        data-testid="cookie-consent-banner"
      >
        <div className="overflow-hidden rounded-2xl border border-primary/25 bg-zinc-950 shadow-2xl shadow-black/80">
          {/* Main Banner */}
          <div className="p-4 sm:p-5">
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div className="hidden h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/15 sm:flex">
                <Cookie className="w-6 h-6 text-primary" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <Cookie className="w-5 h-5 text-primary sm:hidden" />
                  <h3 className="text-base font-bold">Your privacy choices</h3>
                </div>
                <p className="mb-4 text-xs leading-relaxed text-zinc-400 sm:text-sm">
                  We use optional cookies to improve Rastyle, personalise content and measure traffic.
                  Read our{' '}
                  <Link to="/privacy-policy" className="text-primary hover:underline">
                    Privacy Policy
                  </Link>{' '}
                  for more information.
                </p>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                  <button
                    onClick={handleAcceptAll}
                    className="flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-primary/90"
                    data-testid="accept-all-cookies"
                  >
                    <Check className="w-4 h-4" />
                    Accept All
                  </button>
                  <button
                    onClick={handleRejectNonEssential}
                    className="rounded-xl bg-white/10 px-4 py-2.5 text-sm font-medium transition-colors hover:bg-white/20"
                    data-testid="reject-non-essential"
                  >
                    Essential Only
                  </button>
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="col-span-2 flex items-center justify-center gap-1 px-4 py-2 text-xs text-zinc-400 transition-colors hover:text-white sm:col-auto"
                    data-testid="customize-cookies"
                  >
                    Customize
                    {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Close Button (accepts essential only) */}
              <button
                onClick={handleRejectNonEssential}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                aria-label="Close (Essential cookies only)"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Detailed Preferences Panel */}
          <AnimatePresence>
            {showDetails && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="border-t border-white/10 overflow-hidden"
              >
                <div className="max-h-[45vh] space-y-3 overflow-y-auto p-4 sm:p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Shield className="w-5 h-5 text-primary" />
                    <h4 className="font-semibold">Cookie Preferences</h4>
                  </div>

                  {cookieTypes.map((cookie) => (
                    <div 
                      key={cookie.key}
                      className={`p-4 rounded-lg border transition-colors ${
                        preferences[cookie.key] 
                          ? 'bg-primary/10 border-primary/30' 
                          : 'bg-white/5 border-white/10'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h5 className="font-semibold">{cookie.title}</h5>
                            {cookie.required && (
                              <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full">
                                Required
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            {cookie.description}
                          </p>
                          <p className="text-xs text-muted-foreground/70">
                            Examples: {cookie.examples}
                          </p>
                        </div>
                        <button
                          onClick={() => togglePreference(cookie.key)}
                          disabled={cookie.required}
                          className={`relative w-12 h-6 rounded-full transition-colors ${
                            preferences[cookie.key] 
                              ? 'bg-primary' 
                              : 'bg-white/20'
                          } ${cookie.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          aria-label={`Toggle ${cookie.title}`}
                        >
                          <span 
                            className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                              preferences[cookie.key] ? 'left-7' : 'left-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Save Preferences Button */}
                  <div className="flex justify-end pt-4 border-t border-white/10">
                    <button
                      onClick={handleSavePreferences}
                      className="px-6 py-2.5 bg-primary text-black font-semibold rounded-lg hover:bg-primary/90 transition-colors"
                      data-testid="save-cookie-preferences"
                    >
                      Save Preferences
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsentBanner;
